import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  EmptyState,
  Field,
  Input,
  Modal,
  Page,
  QueryState,
  RankedBars,
  Select,
  Stack,
  StatStrip,
  FlaskIcon,
  ClockIcon,
  CheckCircleIcon,
  LayersIcon,
  TrendIcon,
  ShieldIcon,
  type Tone,
} from '@integra/ui';
import type { Asset, Experiment, ExperimentStatus } from '../api/types.js';
import {
  useAssets,
  useCreateExperiment,
  useDestinations,
  useEvaluateExperiment,
  useExperiments,
  useStartExperiment,
} from '../api/hooks.js';

/** The metrics an experiment can be decided on (a subset of channel_metrics). */
const METRICS = [
  'clicks',
  'impressions',
  'engagements',
  'conversions',
  'video_views',
  'reach',
  'shares',
  'saves',
] as const;

const STATUS_TONE: Record<ExperimentStatus, Tone> = {
  draft: 'neutral',
  running: 'info',
  decided: 'success',
  stopped: 'warning',
};

function assetLabel(asset: Asset): string {
  const title =
    typeof asset.data['title'] === 'string' ? (asset.data['title'] as string) : asset.id;
  return `${title} (${asset.type})`;
}

/**
 * Slot-level A/B experimentation (§20.5): run competing creatives on one channel,
 * then auto-promote the winner from MEASURED data. The decision is trustworthy by
 * construction — an experiment with thin, estimated, or too-close data holds and
 * says why rather than inventing a winner (§2.7).
 */
export function ExperimentsPage() {
  const { workspaceId = '' } = useParams();
  const experiments = useExperiments(workspaceId);
  const destinations = useDestinations(workspaceId);
  const assets = useAssets(workspaceId, { state: 'active' });

  const create = useCreateExperiment(workspaceId);
  const start = useStartExperiment(workspaceId);
  const evaluate = useEvaluateExperiment(workspaceId);

  const [open, setOpen] = useState(false);
  const [destinationId, setDestinationId] = useState('');
  const [metric, setMetric] = useState<string>('clicks');
  const [variants, setVariants] = useState<{ assetId: string; label: string }[]>([
    { assetId: '', label: 'A' },
    { assetId: '', label: 'B' },
  ]);

  const destinationList = destinations.data?.data ?? [];
  const assetList = assets.data?.data ?? [];

  const readyVariants = variants.filter((v) => v.assetId !== '');
  const canCreate = destinationId !== '' && readyVariants.length >= 2;

  function setVariant(i: number, patch: Partial<{ assetId: string; label: string }>) {
    setVariants((prev) => prev.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  }

  function submitCreate() {
    if (!canCreate) return;
    create.mutate(
      { destinationId, metric, variants: readyVariants },
      {
        onSuccess: () => {
          setVariants([
            { assetId: '', label: 'A' },
            { assetId: '', label: 'B' },
          ]);
          setDestinationId('');
          setOpen(false);
        },
      },
    );
  }

  const experimentList = experiments.data?.data ?? [];
  const runningCount = experimentList.filter((e) => e.status === 'running').length;
  const decidedCount = experimentList.filter((e) => e.status === 'decided').length;

  return (
    <Page
      title="Experiments"
      icon={FlaskIcon}
      description="A/B two creatives on one channel — INTEGRA auto-promotes the winner from measured results and delists the losers."
      actions={<Button onClick={() => setOpen(true)}>New experiment</Button>}
      stats={
        experimentList.length > 0 ? (
          <StatStrip
            stats={[
              {
                icon: FlaskIcon,
                label: 'Experiments',
                value: experimentList.length,
                tone: 'accent',
              },
              { icon: ClockIcon, label: 'Running', value: runningCount },
              { icon: CheckCircleIcon, label: 'Decided', value: decidedCount },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        {evaluate.data !== undefined && (
          <Alert
            tone={evaluate.data.decision.decided ? 'success' : 'info'}
            title={
              evaluate.data.decision.decided
                ? 'Winner promoted from measured data'
                : 'Holding — not decided on measured data yet'
            }
          >
            <Stack gap="sm">
              <p className="text-sm">{evaluate.data.decision.rationale}</p>
              <RankedBars
                items={evaluate.data.standings.map((s) => ({
                  label: s.label,
                  value: s.measured ?? 0,
                  valueLabel:
                    s.measured === null ? 'no measured data' : s.measured.toLocaleString(),
                }))}
              />
            </Stack>
          </Alert>
        )}
        {evaluate.error instanceof Error && (
          <Alert tone="error" title="Could not evaluate">
            {evaluate.error.message}
          </Alert>
        )}
        {start.error instanceof Error && (
          <Alert tone="error" title="Could not start the experiment">
            {start.error.message}
          </Alert>
        )}

        <QueryState loading={experiments.isLoading} error={experiments.error} what="experiments">
          {experimentList.length === 0 ? (
            <EmptyState
              icon={FlaskIcon}
              title="No experiments yet"
              description="Test two creatives on a channel and let the winner promote itself from measured results."
              action={<Button onClick={() => setOpen(true)}>New experiment</Button>}
              features={[
                { icon: LayersIcon, label: 'Compare two creatives per slot' },
                { icon: TrendIcon, label: 'Winner promoted from measured data' },
                { icon: ShieldIcon, label: 'Thin or too-close results hold' },
              ]}
            />
          ) : (
            <Stack gap="md">
              {experimentList.map((exp: Experiment) => {
                const winner = exp.variants.find((v) => v.id === exp.winnerVariantId);
                return (
                  <Card key={exp.id}>
                    <Stack gap="sm">
                      <Cluster justify="between" align="center">
                        <Cluster gap="sm" align="center">
                          <Badge tone={STATUS_TONE[exp.status]} dot>
                            {exp.status}
                          </Badge>
                          <span className="font-heading text-sm text-navy-700">
                            {exp.metric} on {exp.destinationId}
                          </span>
                        </Cluster>
                        <Cluster gap="sm">
                          {exp.status === 'draft' && (
                            <Button
                              size="sm"
                              loading={start.isPending && start.variables === exp.id}
                              onClick={() => start.mutate(exp.id)}
                            >
                              Start
                            </Button>
                          )}
                          {exp.status === 'running' && (
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={evaluate.isPending && evaluate.variables === exp.id}
                              onClick={() => evaluate.mutate(exp.id)}
                            >
                              Evaluate
                            </Button>
                          )}
                        </Cluster>
                      </Cluster>
                      <p className="text-xs text-ink-muted">
                        {exp.variants.map((v) => v.label).join(' vs ')}
                      </p>
                      {exp.status === 'decided' && (
                        <Alert tone="success" title={`Winner: ${winner?.label ?? 'promoted'}`}>
                          {exp.rationale ?? 'Promoted from measured data.'}
                        </Alert>
                      )}
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          )}
        </QueryState>
      </Stack>

      <Modal
        open={open}
        title="New experiment"
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button loading={create.isPending} disabled={!canCreate} onClick={submitCreate}>
              Create experiment
            </Button>
          </>
        }
      >
        <Stack gap="md">
          <Cluster gap="md" align="end">
            <Field label="Channel slot" htmlFor="exp-destination">
              <Select
                id="exp-destination"
                value={destinationId}
                onChange={(e) => setDestinationId(e.target.value)}
              >
                <option value="">Choose a channel…</option>
                {destinationList.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.id} ({d.status})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Decide on metric" htmlFor="exp-metric">
              <Select id="exp-metric" value={metric} onChange={(e) => setMetric(e.target.value)}>
                {METRICS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Field>
          </Cluster>

          <Stack gap="sm">
            {variants.map((v, i) => (
              <Cluster key={i} gap="md" align="end">
                <Field
                  label={`Variant ${String(i + 1)} asset`}
                  htmlFor={`exp-variant-${String(i)}`}
                >
                  <Select
                    id={`exp-variant-${String(i)}`}
                    value={v.assetId}
                    onChange={(e) => setVariant(i, { assetId: e.target.value })}
                  >
                    <option value="">Choose an asset…</option>
                    {assetList.map((a) => (
                      <option key={a.id} value={a.id}>
                        {assetLabel(a)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Label" htmlFor={`exp-label-${String(i)}`}>
                  <Input
                    id={`exp-label-${String(i)}`}
                    value={v.label}
                    onChange={(e) => setVariant(i, { label: e.target.value })}
                  />
                </Field>
                {variants.length > 2 && (
                  <Button
                    variant="ghost"
                    onClick={() => setVariants((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    Remove
                  </Button>
                )}
              </Cluster>
            ))}
            <Cluster gap="sm">
              <Button
                variant="ghost"
                onClick={() =>
                  setVariants((prev) => [
                    ...prev,
                    { assetId: '', label: String.fromCharCode(65 + prev.length) },
                  ])
                }
              >
                Add variant
              </Button>
            </Cluster>
          </Stack>

          <p className="text-xs text-ink-muted">
            Each variant publishes to the same channel; the winner is decided only from measured
            results, and the losers are delisted automatically.
          </p>
          {create.error instanceof Error && (
            <Alert tone="error" title="Could not create the experiment">
              {create.error.message}
            </Alert>
          )}
        </Stack>
      </Modal>
    </Page>
  );
}
